import { useState } from 'react'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'

import { uploadDocument } from '../../lib/db/db'

type DocumentUploadProps = {
  workspaceId: string
  userId: string
  onUploadComplete?: (documents: Array<{ id: string; name: string }>) => void
}

export const DocumentUpload = ({
  workspaceId,
  userId,
  onUploadComplete,
}: DocumentUploadProps) => {
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  )
  const [isDragging, setIsDragging] = useState(false)

  const handleFileUpload = async (file: File) => {
    try {
      setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }))
      const documentId = await uploadDocument({
        file,
        workspaceId,
        userId,
        onProgress: (progress) => {
          setUploadProgress((prev) => ({ ...prev, [file.name]: progress }))
        },
      })
      setUploadProgress((prev) => {
        const newProgress = { ...prev }
        delete newProgress[file.name]
        return newProgress
      })
      toast(`${file.name} uploaded successfully!`)
      onUploadComplete?.([{ id: documentId, name: file.name }])
    } catch (error) {
      console.error('Error uploading file:', error)
      toast(`Failed to upload ${file.name}`)
      setUploadProgress((prev) => {
        const newProgress = { ...prev }
        delete newProgress[file.name]
        return newProgress
      })
    }
  }

  const handleFiles = (files: FileList) => {
    Array.from(files).forEach((file) => {
      handleFileUpload(file)
    })
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files) return
    handleFiles(files)
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragIn = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragOut = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (!files) return
    handleFiles(files)
  }

  return (
    <div
      onClick={() => document.getElementById('fileInput')?.click()}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`mt-4 flex flex-col items-center p-6 border-2 border-dashed rounded-lg bg-gray-50 cursor-pointer transition-colors ${
        isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
      }`}
    >
      <ArrowUpTrayIcon className="h-12 w-12 text-gray-400 mb-3" />
      <label className="block mb-2 text-lg font-medium text-gray-900">
        Upload New Documents
      </label>
      <p className="mb-4 text-sm text-gray-500">
        Drop files here or click to upload
      </p>
      <input
        id="fileInput"
        type="file"
        onChange={handleInputChange}
        className="hidden"
        multiple
      />
      {Object.entries(uploadProgress).map(([fileName, progress]) => (
        <div key={fileName} className="mt-2 w-full max-w-md">
          <div className="flex justify-between mb-1">
            <span className="text-sm text-gray-600 truncate">{fileName}</span>
            <span className="text-sm text-gray-600">
              {progress.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  )
}
